import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthAPI } from "../../api/authAPI";
import { PROD_BD_API_URL } from "../../api/axiosInstance";
import AuthLayout from "../../components/AuthLayout";
import { useFeedback } from "../../context/FeedbackContext";
import { Box, Button, Typography } from "@mui/material";
import { triggerGa } from "../../utils/gaUtils";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERROR_BORDER = "#973236";
const ERROR_TEXT = "#973236";
const GOOGLE_ICON_URL =
  "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";

const SolidArrowIcon = () => (
  <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
    <path
      d="M2.5 10H15.5M10.5 4.5L16 10L10.5 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GoogleArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="M2.5 10H15.5M10.5 4.5L16 10L10.5 15.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function LoginEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { openFeedbackModal } = useFeedback();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [showSupportCta, setShowSupportCta] = useState(false);
  const [successMessage] = useState(location.state?.successMessage || "");
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  useEffect(() => {
    triggerGa({
      category: "Login",
      action: "page_view",
      label: "login_page",
      page: "login",
      screenName: "login",
    });
  }, []);

  useEffect(() => {
    const stateEmail = location.state?.email;
    const queryEmail = new URLSearchParams(location.search).get("email");
    const prefilledEmail = stateEmail || queryEmail;
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (location.state?.successMessage) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  const handleNext = async () => {
    if (!EMAIL_PATTERN.test(email.trim())) {
      triggerGa({
        category: "Error",
        action: "validation_failed",
        label: "invalid_email",
        page: "login",
        screenName: "login",
      });
      setError("Please enter a valid email address");
      return;
    }
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "email_submit",
      page: "login",
      screenName: "login",
      label3: "login_method=email",
    });

    setError("");
    setShowSupportCta(false);
    setIsSendingOtp(true);
    try {
      await AuthAPI.sendLoginOTP({ email: email.trim().toLowerCase() });
      navigate("/login/otp", {
        state: { email: email.trim().toLowerCase() },
      });
    } catch (apiError) {
      const message =
        apiError?.response?.data?.message || "Failed to send OTP.";
      const isBlockedAccount =
        apiError?.response?.status === 403 ||
        message.toLowerCase().includes("account not found") ||
        message.toLowerCase().includes("pending approval");
      setError(
        isBlockedAccount ? "This account is no longer active." : message,
      );
      setShowSupportCta(isBlockedAccount);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleGoogleLogin = () => {
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "google_login",
      page: "login",
      screenName: "login",
      label3: "login_method=google",
    });
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${PROD_BD_API_URL}oauth-login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthLayout>
      {successMessage ? (
        <Box
          sx={{
            width: "100%",
            border: "1px dashed #d7d7d7",
            borderRadius: "8px",
            p: "20px",
            boxSizing: "border-box",
            color: "#5a5a5a",
            background: "#fafafa",
          }}
        >
          {successMessage}
        </Box>
      ) : null}
      <Box
        sx={{
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          background: "#fbfbfb",
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            m: "0 0 16px",
            fontSize: 12,
            lineHeight: "20px",
            fontWeight: 600,
            letterSpacing: "0.2px",
            textTransform: "uppercase",
            color: "#0d0d0d",
          }}
        >
          Login/Signup
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <Typography
            component="label"
            htmlFor="email"
            sx={{
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              fontWeight: 500,
              color: "#000000",
            }}
          >
            Email address
          </Typography>
          <Box
            component="input"
            id="email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (error) {
                setError("");
                setShowSupportCta(false);
              }
            }}
            placeholder="Enter email address"
            sx={{
              width: "100%",
              height: 40,
              border: error ? `1px solid ${ERROR_BORDER}` : "1px solid #e5e5e5",
              borderRadius: "8px",
              p: "8px 12px",
              boxSizing: "border-box",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: "20px",
              letterSpacing: "0.2px",
              fontWeight: 400,
              color: "#0d0d0d",
              bgcolor: "#ffffff",
              outline: "none",
            }}
          />
          {error ? (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: "18px",
                letterSpacing: "0.2px",
                color: ERROR_TEXT,
                fontWeight: 400,
              }}
            >
              {error}
            </Typography>
          ) : null}
          {showSupportCta ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                component="span"
                onClick={() =>
                  openFeedbackModal("feedback", {
                    brand_name: email.trim(),
                    user_email: email.trim().toLowerCase(),
                  })
                }
                sx={{
                  color: "#ee4d37",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "18px",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                Contact Support
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 12, lineHeight: "18px", color: "#8a8a8a" }}
              >
                if you believe this is a mistake.
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box sx={{ width: 234 }}>
            <Button
              type="button"
              onClick={handleNext}
              disabled={isSendingOtp}
              sx={{
                width: "100%",
                height: 48,
                border: "1px solid #ee4d37",
                borderRadius: "8px",
                background: "#ee4d37",
                color: "#ffffff",
                fontSize: 16,
                lineHeight: "20px",
                fontWeight: 600,
                textTransform: "none",
                cursor: isSendingOtp ? "not-allowed" : "pointer",
                opacity: isSendingOtp ? 0.7 : 1,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  letterSpacing: "0.2px",
                }}
              >
                <span>Next</span>
                <Box
                  sx={{
                    width: 20,
                    minWidth: 20,
                    height: 20,
                    textAlign: "center",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SolidArrowIcon />
                </Box>
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          justifyContent: "center",
          my: 2,
        }}
      >
        <Box sx={{ width: 128, borderTop: "1px solid #e3e3e3" }} />
        <Typography
          sx={{
            m: 0,
            fontSize: 16,
            lineHeight: "24px",
            textTransform: "uppercase",
            color: "#8a8a8a",
            fontWeight: 500,
          }}
        >
          OR
        </Typography>
        <Box sx={{ width: 128, borderTop: "1px solid #e3e3e3" }} />
      </Box>

      <Button
        type="button"
        onClick={handleGoogleLogin}
        sx={{
          width: "100%",
          height: 48,
          border: "none",
          borderRadius: "8px",
          background: "#efefef",
          color: "#5a5a5a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "8px 16px",
          textTransform: "none",
          "&:hover": { background: "#efefef" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            sx={{ width: 24, height: 24 }}
            src={GOOGLE_ICON_URL}
            alt="Google"
          />
          <Typography
            sx={{ fontSize: 16, lineHeight: "20px", fontWeight: 500 }}
          >
            Login with Google
          </Typography>
        </Box>
        <Box
          sx={{
            width: 32,
            minWidth: 32,
            p: "6px",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#5a5a5a",
          }}
        >
          <GoogleArrowIcon />
        </Box>
      </Button>
    </AuthLayout>
  );
}
